import { useState, FormEvent } from 'react';
import styled from 'styled-components';
import colors from '../../../../styles/colors';
import { apiCreateExpansion } from '../../../../api/expansionApi';
import type { Expansion } from '../../../../types/models/expansion';

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Dialog = styled.div`
  background-color: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: gold;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  padding: 24px 20px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  color: gold;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: gold; }
`;

const Textarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  box-sizing: border-box;
  min-height: 70px;
  resize: vertical;
  &:focus { outline: none; border-color: gold; }
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const ToggleBtn = styled.button<{ active: boolean }>`
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.2)')};
  background-color: ${({ active }) => (active ? 'rgba(255,215,0,0.15)' : 'transparent')};
  color: ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.4)')};
  font-size: 12px;
  cursor: pointer;
`;

const ErrorMsg = styled.p`
  color: #dc3545;
  font-size: 13px;
  margin: 8px 0 0;
`;

const BtnSecondary = styled.button`
  padding: 8px 18px;
  background-color: transparent;
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  &:hover:not(:disabled) { background-color: rgba(255,255,255,0.05); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnPrimary = styled.button`
  padding: 8px 20px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background-color: ${colors.bgColorRed}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface ExpansionCreateModalProps {
  onClose: () => void;
  onCreated: (expansion: Expansion) => void;
}

const ExpansionCreateModal: React.FC<ExpansionCreateModalProps> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [imgMode, setImgMode] = useState<'path' | 'file'>('path');
  const [imgPath, setImgPath] = useState('');
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('release_date', releaseDate);
      if (imgMode === 'file' && imgFile) fd.append('expansion_file', imgFile);
      else if (imgPath) fd.append('expansion_path', imgPath);
      if (notes) fd.append('notes', notes);

      const response = (await apiCreateExpansion(fd)) as any;
      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        const firstVal = response.errors[firstKey];
        setError(String(Array.isArray(firstVal) ? firstVal[0] : (firstVal ?? 'Errore di validazione')));
        return;
      }
      onCreated(response?.data ?? response);
      onClose();
    } catch {
      setError('Errore durante il salvataggio. Riprova.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Dialog>
        <ModalHeader>
          <ModalTitle>Nuova Espansione</ModalTitle>
          <CloseBtn type="button" onClick={onClose}>✕</CloseBtn>
        </ModalHeader>

        <ModalBody>
          <form id="exp-create-form" onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Nome *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="es: Base Set 2024"
              />
            </FormGroup>

            <FormGroup>
              <Label>Data rilascio *</Label>
              <Input
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Immagine</Label>
              <ToggleRow>
                <ToggleBtn type="button" active={imgMode === 'path'} onClick={() => setImgMode('path')}>Path</ToggleBtn>
                <ToggleBtn type="button" active={imgMode === 'file'} onClick={() => setImgMode('file')}>File</ToggleBtn>
              </ToggleRow>
              {imgMode === 'path' ? (
                <Input
                  value={imgPath}
                  onChange={(e) => setImgPath(e.target.value)}
                  placeholder="es: expansions/cover.jpg"
                />
              ) : (
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImgFile(e.target.files?.[0] ?? null)}
                />
              )}
            </FormGroup>

            <FormGroup>
              <Label>Note</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Note opzionali..."
              />
            </FormGroup>

            {error && <ErrorMsg>{error}</ErrorMsg>}
          </form>
        </ModalBody>

        <ModalFooter>
          <BtnSecondary type="button" disabled={isSaving} onClick={onClose}>Annulla</BtnSecondary>
          <BtnPrimary type="submit" form="exp-create-form" disabled={isSaving}>
            {isSaving ? 'Creazione...' : 'Crea Espansione'}
          </BtnPrimary>
        </ModalFooter>
      </Dialog>
    </Overlay>
  );
};

export default ExpansionCreateModal;
